import React, { useEffect, useState } from 'react';
import { cancelRescheduleCall, createRescheduleCall, getRescheduleCall } from '../../../api/v2/calls';
import {
  formatCallHoursRangeInViewerTimezone,
  formatScheduledAt,
  isWithinCallHours,
} from '../../../helpers/timezone_utils';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import AvailabilityPicker from '../availability_picker';
import { CalendarIcon, PhoneIcon } from '../call_page_icon';

import styles from './index.module.css';

function firstName(name) {
  if (!name) return '';
  return name.trim().split(' ')[0];
}

const CallReschedule = ({ match }) => {
  const { token } = match.params;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pickingTimes, setPickingTimes] = useState(false);
  const [created, setCreated] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getRescheduleCall(token)
      .then((data) => setInfo(data))
      .catch(() => setInfo(false))
      .finally(() => setLoading(false));
  }, [token]);

  const handleCallNow = () => {
    setSubmitting(true);
    createRescheduleCall(token, { scheduled: false })
      .then(() => setCreated(true))
      .catch((err) => failToast(err.message || 'Could not start this call. Please try again.'))
      .finally(() => setSubmitting(false));
  };

  const handleCancel = () => {
    setSubmitting(true);
    cancelRescheduleCall(token)
      .then(() => {
        setInfo({ ...info, cancelable_call: null });
        setConfirming(false);
      })
      .catch((err) => failToast(err.message || 'Could not cancel this call. Please try again.'))
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <p className={styles.description}>Loading…</p>
        </div>
      </div>
    );
  }

  if (info === false) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <h1 className={styles.title}>Link unavailable</h1>
          <p className={styles.description}>
            This link is invalid or has already been used. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  if (created) {
    const mentorName = firstName(info.other_user_name) || 'Your mentor';
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <h1 className={styles.title}>Call requested</h1>
          <p className={styles.description}>{mentorName} has been notified.</p>
          <a href='/member/conversations' className={styles.back_link}>
            Go to your conversations
          </a>
        </div>
      </div>
    );
  }

  const withinHours = isWithinCallHours(info.mentor_timezone);
  const canCall = info.phone_calls_enabled && info.mentor_accepts_calls;
  const canCallNow = canCall && withinHours;
  const mentorName = firstName(info.other_user_name) || 'your mentor';
  const scheduledLabel = formatScheduledAt(info.cancelable_call?.scheduled_at, info.viewer_timezone);
  const hours = formatCallHoursRangeInViewerTimezone(info.mentor_timezone);

  let title = `Request a call from ${mentorName}?`;
  let description = `If you request a call, we'll text ${mentorName}. They'll have 15 minutes to confirm.`;

  if (!info.phone_calls_enabled) {
    description = 'Phone calls are not available for this account.';
  } else if (!info.mentor_accepts_calls) {
    description = 'This mentor is not currently accepting calls.';
  } else if (!withinHours) {
    title = `It's after hours for ${mentorName}`;
    description = hours
      ? `${mentorName} is reachable between ${hours.startHour} and ${hours.endHour}${hours.abbr ? ` ${hours.abbr}` : ''}. Schedule a call instead and pick a time that works.`
      : `${mentorName} is reachable between 8AM and 8PM. Schedule a call instead and pick a time that works.`;
  }

  let card = (
    <>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.description}>{description}</p>
      <button
        type='button'
        className={`${styles.btn} ${styles.btn_success}`}
        disabled={!canCallNow || submitting}
        aria-disabled={!canCallNow || submitting}
        onClick={() => setPendingAction('call')}
      >
        <PhoneIcon size={16} />
        Continue
      </button>
      <button
        type='button'
        className={`${styles.btn} ${styles.btn_secondary}`}
        disabled={!canCall || submitting}
        onClick={() => setPickingTimes(true)}
      >
        <CalendarIcon />
        Schedule instead
      </button>
    </>
  );

  if (pendingAction === 'call') {
    card = (
      <>
        <h1 className={styles.title}>Request this call?</h1>
        <p className={styles.description}>
          We&apos;ll text {mentorName}. They&apos;ll have 15 minutes to confirm. Nothing is sent until you confirm.
        </p>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_success}`}
          disabled={submitting}
          onClick={handleCallNow}
        >
          <PhoneIcon size={16} />
          {submitting ? 'Sending…' : 'Yes, request the call'}
        </button>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_secondary}`}
          disabled={submitting}
          onClick={() => setPendingAction(null)}
        >
          Go back
        </button>
      </>
    );
  }

  if (info.cancelable_call && !confirming) {
    const when = scheduledLabel ? ` on ${scheduledLabel}` : '';
    card = (
      <>
        <h1 className={styles.title}>Cancel it to reschedule?</h1>
        <p className={styles.description}>
          You already have a call with {info.cancelable_call.other_user_name || 'your mentor'}
          {when}. Cancel that call before choosing a new time.
        </p>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_danger}`}
          disabled={submitting}
          onClick={() => setConfirming(true)}
        >
          Yes, Cancel the Call
        </button>
      </>
    );
  }

  if (confirming) {
    card = (
      <>
        <h1 className={styles.title}>Cancel this call?</h1>
        <p className={styles.description}>Nothing is canceled until you confirm.</p>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_danger}`}
          disabled={submitting}
          onClick={handleCancel}
        >
          {submitting ? 'Processing...' : 'Yes, Cancel the Call'}
        </button>
        <button
          type='button'
          className={`${styles.btn} ${styles.btn_secondary}`}
          disabled={submitting}
          onClick={() => setConfirming(false)}
        >
          Keep it
        </button>
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon} aria-hidden='true'>
          <PhoneIcon />
        </div>
        {card}
      </div>
      {pickingTimes && (
        <AvailabilityPicker
          mentorTimezone={info.mentor_timezone}
          viewerTimezone={info.viewer_timezone}
          mentorFirstName={mentorName}
          otherUserName={info.other_user_name}
          otherUserPhoto={info.other_user_photo}
          blockedSlots={info.blocked_slots}
          submitProposedSlots={(proposedSlots) =>
            createRescheduleCall(token, { scheduled: true, proposed_slots: proposedSlots })
          }
          onClose={() => setPickingTimes(false)}
          onCallCreated={() => {
            setPickingTimes(false);
            setCreated(true);
          }}
        />
      )}
    </div>
  );
};

export default CallReschedule;
